/**
 * Panel "API de <proveedor>" dentro de la tarjeta de Enlaces bancarios (Bind,
 * Banco CMF, Coelsa).
 *
 * Botón "Probar APIs ahora": corre la sonda (autenticación + lecturas, nunca
 * escrituras) y lista los endpoints con su resultado. Las escrituras muestran
 * en cambio su última llamada real (la mide Monitoreo › Proveedores sobre los
 * logs de los servicios que las hacen): OK sólo si esa última llamada salió
 * bien. Debajo, el histórico por horas (6h / 12h / 24h / 7d) de las corridas
 * automáticas cada 30 minutos.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Box, Stack, Typography, Button, Chip, Tooltip, CircularProgress, Alert,
  ToggleButton, ToggleButtonGroup, Table, TableHead, TableBody, TableRow, TableCell, Divider,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { fmtArDateTime } from "../services/monitoring";
import {
  getProbeStatus, getProbeHistory, runProbe, PROBE_TEXT, PROBE_HOURS,
  type ProbeProvider, type ProbeStatusResponse, type ProbeHistory, type ProbeRun, type ProbeStatus, type CatalogEntry,
} from "../services/apiProbe";

const C_OK = "#16A34A";
const C_WARN = "#B45309";
const C_DOWN = "#DC2626";
const C_UNK = "#94A3B8";

function statusColor(s: ProbeStatus): string {
  if (s === "ok") return C_OK;
  if (s === "auth" || s === "client_error" || s === "limited" || s === "not_enabled") return C_WARN;
  if (s === "skipped" || s === "pending" || s === "no_traffic" || s === "unused") return C_UNK;
  return C_DOWN;
}

function fmtPct(x: number | null): string {
  return x == null ? "—" : `${(x * 100).toFixed(x >= 0.999 ? 0 : 2)}%`;
}

function fmtHour(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("es-AR", { timeZone: "America/Argentina/Buenos_Aires", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

/** Franja horaria: un rectángulo por hora; gris sin corridas. */
function HourStrip({ buckets }: { buckets: ProbeHistory["endpoints"][number]["buckets"] }) {
  return (
    <Stack direction="row" spacing={0.25} sx={{ height: 22, alignItems: "stretch" }}>
      {buckets.map((b) => {
        const color = b.uptime == null ? C_UNK : b.uptime >= 1 ? C_OK : b.uptime > 0 ? C_WARN : C_DOWN;
        const label = b.uptime == null
          ? "sin corridas"
          : `${b.n} corrida(s) · respondió ${fmtPct(b.uptime)}${b.p95 != null ? ` · p95 ${Math.round(b.p95)} ms` : ""}`;
        return (
          <Tooltip key={b.h} title={`${fmtHour(b.h)} hs: ${label}`} arrow>
            <Box sx={{ flex: "1 1 0", minWidth: 2, bgcolor: color, borderRadius: 0.5, opacity: 0.85, "&:hover": { opacity: 1 } }} />
          </Tooltip>
        );
      })}
    </Stack>
  );
}

function ResultsTable({ run, catalog, abbrev }: { run: ProbeRun; catalog: CatalogEntry[]; abbrev: string }) {
  const byKey = new Map(run.probes.map((p) => [p.key, p]));
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>#</TableCell>
          <TableCell>Endpoint</TableCell>
          <TableCell>Para qué</TableCell>
          <TableCell>Resultado</TableCell>
          <TableCell align="right">HTTP</TableCell>
          <TableCell align="right">ms</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {catalog.map((c) => {
          const p = byKey.get(c.key);
          const s: ProbeStatus = p?.status ?? (c.impl ? "skipped" : "pending");
          const passive = !c.impl && p?.source === "trafico";
          const unused = !c.impl && (c.unused || p?.source === "ninguna");
          const chipText = c.impl || passive || unused ? PROBE_TEXT[s] : "pendiente de implementación";
          return (
            <TableRow key={c.key} sx={{ opacity: c.impl || passive ? 1 : 0.6 }}>
              <TableCell>{c.n}</TableCell>
              <TableCell sx={{ fontFamily: "monospace", fontSize: 12, whiteSpace: "nowrap" }}>
                {c.method} {abbrev ? c.path.replace(abbrev, "…") : c.path}
              </TableCell>
              <TableCell>
                {c.label}
                {!c.impl && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {unused ? "no se sondea · sin uso" : "no se sondea · última llamada real"}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Chip size="small" label={chipText} sx={{ bgcolor: statusColor(s), color: "#fff", fontWeight: 700 }} />
                {(c.impl || passive || unused) && p?.note && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25, maxWidth: 360 }}>
                    {passive && p.ts ? `${fmtArDateTime(p.ts)} · ` : ""}{p.note}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right" sx={{ fontFamily: "monospace" }}>{p?.http ?? ""}</TableCell>
              <TableCell align="right" sx={{ fontFamily: "monospace" }}>{p && p.ms ? Math.round(p.ms) : ""}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

/** Texto de la tarjeta: qué se sondea y qué no, por proveedor. */
const PROVIDER_TEXT: Record<ProbeProvider, { title: string; what: string; writes: string }> = {
  bind:   { title: "API de Bind",
            what: "Sólo login y lecturas con la cuenta propia",
            writes: "los POST y el DELETE nunca se llaman" },
  cmf:    { title: "API de Banco CMF",
            what: "Sólo token y lecturas (saldos, movimientos, consulta de transferencia) con las credenciales propias",
            writes: "la transferencia nunca se llama" },
  coelsa: { title: "API de Coelsa",
            what: "Sólo token (perfiles debin y alias) y lecturas (listado y búsqueda de DEBINes, consulta de alias) con las credenciales propias",
            writes: "las escrituras (confirmar DEBIN, alias y CVU) nunca se llaman" },
};

export default function ApiProbePanel({ provider }: { provider: ProbeProvider }) {
  const [status, setStatus] = useState<ProbeStatusResponse | null>(null);
  const [statusErr, setStatusErr] = useState<string | null>(null);
  const [run, setRun] = useState<ProbeRun | null>(null);
  const [running, setRunning] = useState(false);
  const [runErr, setRunErr] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [hours, setHours] = useState<number>(24);
  const [hist, setHist] = useState<ProbeHistory | null>(null);
  const [histErr, setHistErr] = useState<string | null>(null);
  const text = PROVIDER_TEXT[provider];

  const loadStatus = useCallback(async () => {
    try {
      const s = await getProbeStatus(provider);
      setStatus(s); setStatusErr(null);
      if (s.last && !run) setRun(s.last);
    } catch (e) {
      setStatusErr(e instanceof Error ? e.message : String(e));
    }
  }, [provider, run]);

  useEffect(() => { void loadStatus(); }, [loadStatus]);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setHist(null); setHistErr(null);
    getProbeHistory(provider, hours)
      .then((h) => { if (alive) setHist(h); })
      .catch((e) => { if (alive) setHistErr(e instanceof Error ? e.message : String(e)); });
    return () => { alive = false; };
  }, [provider, open, hours]);

  const onRun = async () => {
    setRunning(true); setRunErr(null); setOpen(true);
    try {
      const r = await runProbe(provider);
      setRun(r);
      void loadStatus();
    } catch (e) {
      setRunErr(e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(false);
    }
  };

  const last = run ?? status?.last ?? null;
  const state = last?.summary?.state;
  const stateColor = state === "ok" ? C_OK : state === "auth" ? C_WARN : state === "down" ? C_DOWN : C_UNK;
  const stateText = state === "ok" ? "APIs respondiendo" : state === "auth" ? "rechaza credenciales"
    : state === "down" ? `caída: ${last?.summary.down.join(", ")}` : "sin datos";

  return (
    <Box sx={{ mt: 1.5 }}>
      <Divider sx={{ mb: 1 }} />
      <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{status?.label ?? text.title}</Typography>
        {last && (
          <Chip size="small" label={stateText} sx={{ bgcolor: stateColor, color: "#fff", fontWeight: 700 }} />
        )}
        {last && (
          <Typography variant="caption" color="text.secondary">
            última sonda {fmtArDateTime(last.ts)} ({last.trigger === "manual" ? `manual, ${last.operator}` : "automática"})
            {status ? ` · cada ${status.every_minutes} min` : ""}
          </Typography>
        )}
        {status && !status.enabled && (
          <Typography variant="caption" color="text.secondary">
            {status.disabled_note ?? "sin acceso desde este entorno"}
          </Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" variant="outlined" startIcon={running ? <CircularProgress size={14} /> : <PlayArrowIcon />}
                disabled={running || (status != null && !status.enabled)} onClick={() => void onRun()}>
          Probar APIs ahora
        </Button>
        <Button size="small" onClick={() => setOpen((v) => !v)}>{open ? "Ocultar" : "Detalle"}</Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
        {text.what}{status ? ` (${status.subject})` : ""}; {text.writes}: para ellas se muestra la última llamada real de
        nuestros servicios (Monitoreo › Proveedores, cada 5 min), OK sólo si esa llamada salió bien. "Responde" cuenta
        también los 4xx: la pregunta es si la API está viva.
      </Typography>
      {statusErr && <Alert severity="warning" sx={{ mt: 1 }}>{statusErr}</Alert>}
      {runErr && <Alert severity="error" sx={{ mt: 1 }}>{runErr}</Alert>}

      {open && (
        <Stack spacing={2} sx={{ mt: 1.5 }}>
          {last?.error && <Alert severity="error">{last.error}</Alert>}
          {last && status && <ResultsTable run={last} catalog={status.catalog} abbrev={status.path_abbrev} />}

          <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Histórico por hora</Typography>
            <ToggleButtonGroup size="small" exclusive value={hours} onChange={(_e, v) => { if (v) setHours(v); }}>
              {PROBE_HOURS.map((h) => (
                <ToggleButton key={h} value={h}>{h === 168 ? "7 d" : `${h} h`}</ToggleButton>
              ))}
            </ToggleButtonGroup>
            {!hist && !histErr && <CircularProgress size={16} />}
            {hist && (
              <Typography variant="caption" color="text.secondary">
                {hist.runs} corrida(s) desde {fmtArDateTime(hist.from)}
              </Typography>
            )}
          </Stack>
          {histErr && <Alert severity="error">{histErr}</Alert>}
          {hist && (
            <Stack spacing={1}>
              {hist.endpoints.map((e) => (
                <Box key={e.key}>
                  <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.25 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 220 }}>{e.n}. {e.label}</Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                      responde <strong>{fmtPct(e.uptime)}</strong> · {e.runs} corrida(s)
                      {e.p95 != null && ` · p95 ${Math.round(e.p95)} ms`}
                    </Typography>
                  </Stack>
                  <HourStrip buckets={e.buckets} />
                </Box>
              ))}
            </Stack>
          )}
          {status && (
            <Typography variant="caption" color="text.secondary">
              No sondeados (escrituras): {status.catalog.filter((c) => !c.impl).map((c) => `${c.n}. ${c.label}${c.via ? ` (${c.via})` : c.unused ? " (sin uso)" : ""}`).join(" · ")}.
              Para sondearlos sin ejecutar nada habría que mandar cuerpos inválidos a propósito; se decidió no hacerlo. Su fila
              refleja la última llamada real que hizo el servicio indicado, según sus logs; no entran en el histórico por hora.
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
}
